<template>
  <div>
    <div v-if="showSelect">
      <ValidationProvider
        vid="department"
        :name="$t('department')"
        :rules="{ required: true }"
        v-slot="{ errors }"
      >
        <v-autocomplete
          v-if="suborder.orderProductDepartment.length <= 0"
          class="secondary--text"
          autocomplete="off"
          item-text="name"
          item-value="pk"
          :key="`department-${suborder.pk}`"
          outlined
          prepend-inner-icon="fa-plus"
          :error-messages="errors[0]"
          :items="departments"
          :label="$t('department')"
          @click:prepend-inner="dialog = true"
          @change="submit(suborder.pk, department)"
          v-model="department"
        ></v-autocomplete>
        <div v-else>
          <v-chip
            close
            color="primary"
            @click:close="
              removeAssignation(suborder.orderProductDepartment[0].pk)
            "
          >
            {{
              getDepartamentByPK(suborder.orderProductDepartment[0].department)
            }}
          </v-chip>
        </div>
      </ValidationProvider>
    </div>
    <v-alert v-else type="info" outlined color="primary">
      <span class="primary--text font-weight-black">
        {{ $t('assignProduction_no') }}
      </span>
    </v-alert>
    <dial-department v-if="dialog" @done="newDepartment" v-model="dialog" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import DialDepartment from '@/components/department/modal.vue'

export default {
  components: {
    DialDepartment
  },
  props: {
    showSelect: {
      type: Boolean,
      default: false
    },
    suborder: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      dialog: false,
      department: '',
      departments: [],
      assigned: [],
      assignedObject: []
    }
  },
  computed: {
    ...mapGetters({
      company: 'company/getCompanyData'
    })
  },
  methods: {
    ...mapActions({
      showConfirmation: 'confirmation/confirmationValue'
    }),
    /**
     * Método para consultar los departamentos asociados a la empresa
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    async getDepartments() {
      this.departments = []
      try {
        const query = await this.$api.department.list({
          opt: {
            params: {
              company_id: this.company.pk
            }
          }
        })
        this.departments = query.data.results
      } catch (error) {
        console.error(error)
      }
    },
    /**
     * Método para crear un nuevo departamento desde el formulario
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    newDepartment(evt) {
      this.department = evt.pk
      this.getDepartments()
    },
    /**
     * Método para crear el registro con la relación entre la suborden y el
     * departamento
     * @param {Number} departmentPK - ID del departamento
     * @param {Number} suborderPK - ID de la suborden
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    async submit(suborderPK, departmentPK) {
      try {
        await this.$api.sale.assignedDepartment
          .create({
            form: {
              orderProduct: suborderPK,
              department: departmentPK
            }
          })
          .then((response) => {
            if (response.status === 201) {
              this.$toast.success(
                this.$t('assigned', { value: this.$tc('department', 1) })
              )
              const { data } = response
              const formatedData = {
                pk: data.pk,
                orderProduct: data.orderProduct,
                department: data.department
              }
              this.suborder.orderProductDepartment = [formatedData]
              this.$emit('updateAssignDepartment')
            }
          })
      } catch (error) {
        console.error(error)
      }
    },
    /**
     * Método para crear el registro con la relación entre la suborden y el
     * departamento
     * @param {Number} assignationPK - ID de la asignación entre suborden y
     *                                 departamento
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    removeAssignation(assignationPK) {
      this.showConfirmation({
        show: true,
        model: `${this.$t('assigned', { value: this.$tc('department', 1) })}`,
        title: this.$tc('delete', 1),
        type: 'error white--text',
        content: this.$t('confirm_delete'),
        alert: `${this.$t('success', {
          model: `${this.$t('assigned', { value: this.$tc('department', 1) })}`,
          action: this.$tc('delete', 2)
        })}`,
        confirm: () => {
          this.$api.sale.assignedDepartment
            .remove({ pk: assignationPK })
            .then((response) => {
              if (response.status === 200 || response.status === 204) {
                this.suborder.orderProductDepartment = []
                this.department = ''
              }
            })
            .catch((error) => {
              console.error(error)
            })
        }
      })
    },
    /**
     * Método para localizar el departamento asignado a la suborden de la
     * colección de departamentos y devolver el nombre del mismo
     * @param {Number} pk - ID del departamento
     *
     * @return {String}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    getDepartamentByPK(pk) {
      const departmentFounded = this.departments.find(
        (department) => department.pk === pk
      )
      return departmentFounded?.name || ''
    }
  },
  mounted() {
    this.getDepartments()
  }
}
</script>
<style lang="sass">
.assignDepartment
  display: flex
  flex-wrap: wrap
  margin-bottom: 16px
  border-radius: 10px
  padding: 0 .5rem

.theme--light
  .assignDepartment
    background-color: rgb(0 0 0 / 3%)
    border: 1px solid rgb(0 0 0 / 0.26)
    border-left: 5px solid var(--v-primary-base)

.theme--dark
  .assignDepartment
    background-color: rgb(40 40 40)
    border: 1px solid rgb(255 255 255 / 0.16)
    border-left: 5px solid var(--v-primary-base)

.assignDepartment__title
  color: var(--v-primary-base)
  font-family: "Roboto", sans-serif
  font-size: 1.25rem
  font-weight: 900
  letter-spacing: 0.0125em
  line-height: 2rem
  @media (600px > width)
    font-size: 1rem
    letter-spacing: 0.009375em
    line-height: 1.75rem

.assignDepartment__body
  color: var(--v-primary-base)
  font-size: .9rem
  font-weight: 500
.assignDepartment__text
  font-size: 1rem
  font-weight: 400
</style>
